// 📐 Areas — the fifth tab on the Canvas Manager Audit page (Oct 23, 2026).
//
// One home for areas: Today's (daily areas for today + any older ones still
// open, with the day's map), Standing (the areas that stay until removed), and
// Review (week / month). The Team View's table, the Today tab's board and the
// home widget are untouched — this tab is where "find the areas" lands.

import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../../lib/supabase";
import { useAuth } from "../../../context/AuthContext";
import { isCrewManager } from "../../crew/crewAuth";
import { useTabParam } from "../../../lib/useTabParam";
import { CANVASABLE_ROLES, todayET } from "../../../lib/dailyCommitments";
import { fetchAreaOutlines, fetchDayStops, useAreaStats, type AreaOutline } from "../../../lib/canvasAreas";
import { areaDayState, areaIdsKey, type DayStop } from "../../../lib/dailyAreas";
import AreaBoard from "./AreaBoard";
import AreaReview from "./AreaReview";
import OpenPastAreas from "./OpenPastAreas";
import DayAreasMap, { dayMapAreas } from "./DayAreasMap";

interface Person { id: string; full_name: string; role: string }
const ROLE_ORDER: Record<string, number> = { asm: 0, team_lead: 1, restore_manager: 2, canvasser: 3, roof_specialist: 4 };

export default function AreasMode() {
  const { member } = useAuth();
  const canWrite = isCrewManager(member);
  const [view, setView] = useTabParam<"today" | "standing" | "review">("view", "today", ["today", "standing", "review"]);
  const today = todayET();
  const { areas, error, reload } = useAreaStats();
  const [people, setPeople] = useState<Person[]>([]);
  const [outlines, setOutlines] = useState<Map<string, AreaOutline> | null>(null);
  const [stops, setStops] = useState<DayStop[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from("team_roster").select("id, full_name, role")
        .eq("is_active", true).eq("on_leave", false).in("role", [...CANVASABLE_ROLES, "roof_specialist"]);
      if (cancelled) return;
      setPeople(((data ?? []) as Person[]).sort((a, b) =>
        (ROLE_ORDER[a.role] ?? 9) - (ROLE_ORDER[b.role] ?? 9) || a.full_name.localeCompare(b.full_name)));
    })();
    return () => { cancelled = true; };
  }, []);

  // Keyed on the set of area ids, not the live stats array (which refreshes
  // every few seconds on a knocking day), so the map doesn't refetch and re-fit.
  const areaKey = areaIdsKey(areas);
  useEffect(() => {
    if (view !== "today") return;
    let cancelled = false;
    fetchAreaOutlines().then((m) => { if (!cancelled) setOutlines(m); }).catch(() => {});
    fetchDayStops(today).then((s) => { if (!cancelled) setStops(s); }).catch(() => { if (!cancelled) setStops((prev) => prev ?? []); });
    return () => { cancelled = true; };
  }, [view, today, areaKey]);

  const counts = useMemo(() => {
    const xs = areas ?? [];
    return {
      today: xs.filter((a) => areaDayState(a, today) === "today").length,
      past: xs.filter((a) => areaDayState(a, today) === "past").length,
      standing: xs.filter((a) => !a.for_date).length,
    };
  }, [areas, today]);

  const mapAreas = useMemo(() => (areas && outlines ? dayMapAreas(areas, outlines, today) : []), [areas, outlines, today]);

  const tab = (v: typeof view, label: string) => (
    <button type="button" onClick={() => setView(v)}
      className={`rounded-full px-3 py-1.5 text-xs font-bold ${view === v ? "bg-slate-900 text-white" : "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}>
      {label}
    </button>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {tab("today", `Today's areas${areas ? ` · ${counts.today}${counts.past ? ` +${counts.past} open` : ""}` : ""}`)}
        {tab("standing", `Standing areas${areas ? ` · ${counts.standing}` : ""}`)}
        {tab("review", "Review")}
        {canWrite && view !== "review" && (
          <a href="/canvas?assign=1" target="_blank" rel="noopener" className="ml-auto rounded-md bg-slate-900 px-3 py-1.5 text-xs font-bold text-white">
            ✏️ Draw a new area ↗
          </a>
        )}
      </div>

      {view === "today" && (
        <>
          <OpenPastAreas areas={areas} day={today} canWrite={canWrite} onChanged={() => void reload()} />
          <DayAreasMap
            areas={mapAreas}
            stops={stops ?? []}
            title="Today's map"
            subtitle="Today's daily areas in color · standing areas dashed · every build, adjuster meeting and reinspection pinned"
            empty={areas ? "No areas drawn for today yet — Area here on the Today tab, or draw one on the canvas." : "Loading…"}
            right={<a href="/canvas?areas=on" target="_blank" rel="noopener" className="text-[11px] font-bold text-orange-700">Open on canvas ↗</a>}
          />
          <AreaBoard areas={areas} error={error} people={people} canWrite={canWrite} day={today} filter="daily"
            title="📐 Today's areas" onChanged={() => void reload()} onRetry={() => void reload()} />
        </>
      )}

      {view === "standing" && (
        <AreaBoard areas={areas} error={error} people={people} canWrite={canWrite} day={today} filter="standing"
          title="📐 Standing areas" onChanged={() => void reload()} onRetry={() => void reload()} />
      )}

      {view === "review" && <AreaReview />}
    </div>
  );
}
